import { useState, useRef, useEffect } from 'react';
import { logger } from '../utils/logger';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number; // 觸發滑動的最小距離
  maxSwipe?: number; // 最大滑動距離
  enabled?: boolean; // 是否啟用滑動
  dampingFactor?: number; // 阻尼係數
}

interface SwipeGestureState {
  isSwiping: boolean;
  swipeDistance: number;
  swipeDirection: 'left' | 'right' | null;
  isAnimating: boolean; // 動畫進行中標記
  isResetting: boolean; // 重置中標記（用於禁用 transition）
}

interface SwipeGestureResult extends SwipeGestureState {
  swipeRef: React.RefObject<HTMLDivElement>;
}

const useSwipeGesture = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 80,
  maxSwipe = 200,
  enabled = true,
  dampingFactor = 0.3
}: SwipeGestureOptions): SwipeGestureResult => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // 使用 ref 來綁定到 DOM 元素
  const swipeRef = useRef<HTMLDivElement>(null);

  // 使用 ref 存儲 state 值，避免 useEffect 依賴陣列問題
  const swipeDistanceRef = useRef(0);
  const swipeDirectionRef = useRef<'left' | 'right' | null>(null);
  const callbacksRef = useRef({ onSwipeLeft, onSwipeRight });

  // 存儲待執行的換頁 callback（在 transitionend 時執行）
  const pendingPageChangeRef = useRef<(() => void) | null>(null);

  // 使用 ref 來儲存觸摸開始位置，避免在移動過程中重新計算
  const touchStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);

  const isSwipingRef = useRef(false);
  const isAnimatingRef = useRef(false);
  const directionLockedRef = useRef(false);

  // 同步 state 到 ref，避免閉包陷阱
  useEffect(() => {
    swipeDistanceRef.current = swipeDistance;
    swipeDirectionRef.current = swipeDirection;
    isAnimatingRef.current = isAnimating;
  }, [swipeDistance, swipeDirection, isAnimating]);

  // 更新 callbacks ref
  useEffect(() => {
    callbacksRef.current = { onSwipeLeft, onSwipeRight };
  }, [onSwipeLeft, onSwipeRight]);

  // 使用原生事件監聽器避免 passive event 問題
  useEffect(() => {
    const element = swipeRef.current;
    if (!element || !enabled) return;

    // 處理觸摸開始
    const handleTouchStart = (e: TouchEvent) => {
      // 動畫期間不處理新的觸摸
      if (!enabled || isAnimatingRef.current) return;

      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };

      // 重置狀態
      setSwipeDistance(0);
      setSwipeDirection(null);
      setIsSwiping(false);
      isSwipingRef.current = false;
      directionLockedRef.current = false;
    };

    // 處理觸摸移動
    const handleTouchMove = (e: TouchEvent) => {
      if (!enabled || !touchStartRef.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // 檢查是否應該鎖定為水平滑動
      if (!directionLockedRef.current) {
        if (absDeltaX > 10 || absDeltaY > 10) {
          if (absDeltaX > absDeltaY && absDeltaX > 15) {
            // 水平滑動佔主導，鎖定為滑動手勢
            directionLockedRef.current = true;
            isSwipingRef.current = true;
            setIsSwiping(true);
          } else if (absDeltaY > absDeltaX) {
            // 垂直滑動佔主導，不處理
            return;
          }
        }
      }

      // 只有在確定是水平滑動時才處理
      if (isSwipingRef.current) {
        // 只在事件可取消時才調用 preventDefault，避免警告
        if (e.cancelable) {
          e.preventDefault();
        }

        // 計算滑動方向
        const direction: 'left' | 'right' = deltaX > 0 ? 'right' : 'left';
        setSwipeDirection(direction);

        // 計算滑動距離，套用阻尼效果
        let distance = deltaX;
        if (absDeltaX > threshold) {
          // 超過閾值後套用阻尼
          const excess = absDeltaX - threshold;
          distance = deltaX > 0 
            ? threshold + excess * dampingFactor
            : -threshold - excess * dampingFactor;
        }
        
        // 限制最大滑動距離
        distance = Math.max(-maxSwipe, Math.min(maxSwipe, distance));
        setSwipeDistance(distance);
      }
    };

    // 處理觸摸結束
    const handleTouchEnd = () => {
      if (!enabled || !touchStartRef.current) return;

      // 使用 ref 讀取最新值，避免閉包陷阱
      const currentDistance = swipeDistanceRef.current;
      const currentDirection = swipeDirectionRef.current;
      const wasSwipingBeforeEnd = isSwipingRef.current;

      // 重置觸摸狀態
      touchStartRef.current = null;
      isSwipingRef.current = false;
      directionLockedRef.current = false;
      setIsSwiping(false);

      // 檢查是否達到觸發閾值
      if (wasSwipingBeforeEnd && Math.abs(currentDistance) >= threshold) {
        // 1. 啟動滑動到位動畫
        setIsAnimating(true);
        isAnimatingRef.current = true;

        // 計算目標距離（完整滑動一個區間）
        // 容器結構：[Prev 33.333%][Current 33.333%][Next 33.333%]（總寬 300%）
        // 當前位置：translateX(-33.333%)，顯示 Current
        // 目標位置（向左滑）：translateX(-66.666%)，顯示 Next
        //
        // 位置差距：-66.666% - (-33.333%) = -33.333%（相對於容器自身）
        // 換算成像素：-33.333% × 容器寬度 = -33.333% × (3 × 父容器寬度) = -父容器寬度
        //
        // 因此，向左滑一個區間需要移動 -父容器寬度 px
        // 向右滑一個區間需要移動 +父容器寬度 px
        const containerWidth = swipeRef.current?.offsetWidth || 0;
        const parentWidth = swipeRef.current?.parentElement?.offsetWidth || window.innerWidth;
        const targetDistance = currentDirection === 'left'
          ? -parentWidth
          : parentWidth;

        logger.dev('[useSwipeGesture] Animation start:', {
          direction: currentDirection,
          containerWidth,
          parentWidth,
          currentDistance,
          targetDistance,
          windowWidth: window.innerWidth
        });

        setSwipeDistance(targetDistance);

        // 2. 保存換頁 callback，等待 transitionend 事件觸發
        // 關鍵：使用 transitionend 而非 setTimeout，確保動畫真正完成後才換頁
        const callbacks = callbacksRef.current;
        const direction = currentDirection;

        pendingPageChangeRef.current = () => {
          logger.dev('[useSwipeGesture] transitionend, triggering page change:', {
            direction,
            willTriggerCallback: direction === 'left' ? !!callbacks.onSwipeLeft : !!callbacks.onSwipeRight
          });

          // 關鍵修復：使用多幀更新確保順序
          // 1. 先禁用 transition（立即生效）
          setIsResetting(true);

          // 2. 觸發換頁 callback（改變 date，生成新的 prev/current/next 區間）
          logger.dev('[useSwipeGesture] Triggering page change callback');

          if (direction === 'left' && callbacks.onSwipeLeft) {
            callbacks.onSwipeLeft();
          } else if (direction === 'right' && callbacks.onSwipeRight) {
            callbacks.onSwipeRight();
          }

          // 3. 等待下一幀，確保 React 已經處理了 date 更新
          requestAnimationFrame(() => {
            logger.dev('[useSwipeGesture] Resetting swipeDistance to 0 (without transition)');

            // 4. 重置 swipeDistance 到 0（此時資料已經是新的）
            setSwipeDistance(0);
            setSwipeDirection(null);
            setIsAnimating(false);
            isAnimatingRef.current = false;

            // 5. 再等一幀，恢復 transition
            requestAnimationFrame(() => {
              setIsResetting(false);
              logger.dev('[useSwipeGesture] Page change completed');
            });
          });

          // 清除 pending callback
          pendingPageChangeRef.current = null;
        };
      } else {
        // 未達閾值，回彈到原位
        setSwipeDistance(0);
        setSwipeDirection(null);
      }
    };

    // 處理觸摸取消（來電、系統手勢等）
    const handleTouchCancel = () => {
      // 重置所有狀態，但不觸發回調
      touchStartRef.current = null;
      isSwipingRef.current = false;
      directionLockedRef.current = false;
      setIsSwiping(false);
      setSwipeDistance(0);
      setSwipeDirection(null);
    };

    // 儲存事件處理器引用，確保清理時使用相同引用
    const handlers = {
      touchstart: handleTouchStart,
      touchmove: handleTouchMove,
      touchend: handleTouchEnd,
      touchcancel: handleTouchCancel
    };

    // 添加事件監聽器，明確設置 passive 屬性
    element.addEventListener('touchstart', handlers.touchstart, { passive: true });
    element.addEventListener('touchmove', handlers.touchmove, { passive: false }); // 允許 preventDefault
    element.addEventListener('touchend', handlers.touchend, { passive: true });
    element.addEventListener('touchcancel', handlers.touchcancel, { passive: true });

    // 清理函數 - 使用相同的函數引用
    return () => {
      element.removeEventListener('touchstart', handlers.touchstart);
      element.removeEventListener('touchmove', handlers.touchmove);
      element.removeEventListener('touchend', handlers.touchend);
      element.removeEventListener('touchcancel', handlers.touchcancel);
    };
  }, [enabled, threshold, maxSwipe, dampingFactor]); // 移除 state 和 callback 依賴

  // 監聽 transitionend 事件，確保動畫完成後才觸發換頁
  useEffect(() => {
    const element = swipeRef.current;
    if (!element) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      // 只處理 transform 屬性的 transition
      if (e.propertyName !== 'transform') return;

      // 如果有待執行的換頁 callback，執行它
      if (pendingPageChangeRef.current) {
        pendingPageChangeRef.current();
      }
    };

    element.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      element.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, []);

  return {
    swipeRef,
    isSwiping,
    swipeDistance,
    swipeDirection,
    isAnimating,
    isResetting
  };
};

export default useSwipeGesture;